import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Sector,
} from 'recharts';
import {
  Box,
  Typography,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material';
import ErrorHandler from '@Root/ErrorHandler';
import LabelViewExtendService from '@Helpers/URLHelpers/WeboutService/views/LabelViewExtendService';
import './LabelViewOverViewChart.scss';

export default function LabelViewOverViewChart(props) {
  const [, setLabelAnalyticsData] = useState({});
  const [data, setData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const defaultFilters = {
    labelViewFilter: {
      labelId: props?.labelData?.id,
      registryType: props?.labelData?.registryType,
      targetTypeList: ['task', 'story', 'epic', 'ticket'],
    },
  };

  const [filters] = useState(defaultFilters);
  const labelViewExtendService = new LabelViewExtendService();
  const errorHandler = new ErrorHandler();
  const className = 'labelViewOverViewChart';

  const COLORS_MAP = {
    task: 'var(--color-primaryBlue)',
    story: 'var(--color-primaryGold)',
    epic: 'var(--color-primaryGreen)',
    ticket: 'var(--color-primaryOrange)',
  };

  useEffect(() => {
    renderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.labelData?.id, props?.labelData?.registryType]);

  const renderData = async () => {
    try {
      setIsLoading(true);
      const resp = await labelViewExtendService.getItemCountByLabelIdAndTargetTypeAndRegistryType(filters);
      const responseJson = JSON.parse(resp?.body);
      setLabelAnalyticsData(responseJson);

      const chartDataObj = [
        { name: 'Task', value: responseJson?.task || 0 },
        { name: 'Story', value: responseJson?.story || 0 },
        { name: 'Epic', value: responseJson?.epic || 0 },
        { name: 'Ticket', value: responseJson?.ticket || 0 },
      ].filter(item => item.value > 0);

      setData(chartDataObj);
    } catch (err) {
      errorHandler.handleError(err);
    }
    setIsLoading(false);
  };

  const renderActiveShape = (p) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = p;
    return (
      <g style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' }}>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke="none"
        />
      </g>
    );
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, name, value }) => {
    const radius = outerRadius + 24;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const startX = cx + radius * cos;
    const startY = cy + radius * sin;
    const midX = startX + (cos >= 0 ? 15 : -15);
    const midY = startY;
    const endX = midX + (cos >= 0 ? 30 : -30);
    const endY = midY;
    const valueGap = cos >= 0 ? 10 : -10;
    const color = COLORS_MAP[name.toLowerCase()] || 'var(--color-primaryGrey)';

    return (
      <g>
        <path
          d={`M${cx + outerRadius * cos},${cy + outerRadius * sin} L${startX},${startY} Q${midX},${midY} ${endX},${endY}`}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
        <text
          x={endX + valueGap}
          y={endY}
          fill={color}
          fontWeight="600"
          fontSize={13}
          textAnchor={cos >= 0 ? 'start' : 'end'}
          dominantBaseline="middle"
        >
          {value}
        </text>
      </g>
    );
  };

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Paper className={className} elevation={0}>
      <Box className={`${className}__header`}>
        <Box className={`${className}__header__left`}>
          <Typography variant="subtitle1" className={`${className}__title`}>
            Item Wise Overview
          </Typography>
          {total > 0 && (
            <Chip
              label={`Total: ${total}`}
              size="small"
              className={`${className}__totalChip`}
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      </Box>

      <Box className={`${className}__body`}>
        {isLoading ? (
            <Box className={`${className}__loader`}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="textSecondary">
                Loading distribution...
              </Typography>
            </Box>
        ) : data.length === 0 ? (
          <Box className={`${className}__empty`}>
            <Typography variant="body2" className={`${className}__empty__text`}>
              No items found for this label
            </Typography>
          </Box>
        ) : (
          <Box className={`${className}__chartWrap`}>
            <Box className={`${className}__chart`}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={55}
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    label={renderCustomizedLabel}
                    labelLine={false}
                    paddingAngle={2}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS_MAP[entry.name.toLowerCase()] || 'var(--color-primaryGrey)'}
                        stroke="none"
                        opacity={activeIndex !== null && activeIndex !== index ? 0.35 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [`${v}`, n]}
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid var(--color-darkBorder)',
                      background: 'var(--color-componentBackground)',
                      color: 'var(--color-text)',
                      padding: '0.5rem 0.75rem',
                      fontSize: 12
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box className={`${className}__legend`}>
              {data.map((entry, i) => (
                <Box key={i} className={`${className}__legend__item`}>
                  <span
                    className={`${className}__legend__swatch`}
                    style={{ backgroundColor: COLORS_MAP[entry.name.toLowerCase()] || 'var(--color-primaryGrey)' }}
                  />
                  <Typography variant="caption" className={`${className}__legend__label`}>
                    {entry.name}
                  </Typography>
                  <Typography variant="caption" className={`${className}__legend__value`}>
                    {entry.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
